import * as React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import OnlinePredictionRoundedIcon from '@mui/icons-material/OnlinePredictionRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { AdminGeneralInfoResult } from '../../../../models/adminGeneral';
import { Delta } from '../Delta';
import { useNavigate } from 'react-router-dom';

export type UsersCardProps = { data: AdminGeneralInfoResult | null };

export const UsersCard: React.FC<UsersCardProps> = React.memo(({ data }) => {
    const navigate = useNavigate();
    return (
        <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: '8px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate('/app/users')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/users'); } }}
                >
                    <PeopleAltRoundedIcon fontSize="small" />
                    <Typography level="title-md">Пользователи</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Tooltip title="Онлайн пользователи">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <WifiRoundedIcon fontSize="small" sx={{ color: 'danger.600' }} />
                            <Typography level="title-md" sx={{ color: 'danger.600' }}>{data?.online ?? '-'}</Typography>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Онлайн авторизованные">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <VerifiedUserRoundedIcon fontSize="small" sx={{ color: 'primary.600' }} />
                            <Typography level="title-md" sx={{ color: 'primary.600' }}>{data?.onlineLoggedIn ?? '-'}</Typography>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Онлайн активные (авторизованные)">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <OnlinePredictionRoundedIcon fontSize="small" sx={{ color: 'success.600' }} />
                            <Typography level="title-md" sx={{ color: 'success.600' }}>{data?.onlineLoggedInActive ?? '-'}</Typography>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
                <Tooltip title="Всего пользователей">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <GroupsRoundedIcon fontSize="small" sx={{ color: 'text.primary' }} />
                        <Typography level="h3">{data?.totalUsers ?? '-'}</Typography>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Tooltip title="Профит за этот (прошлый) месяцы" disablePortal={false}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PersonAddAltRoundedIcon fontSize="small" sx={{ color: 'success.600' }} />
                        <Typography level="title-lg" sx={{
                            color: (data && (data.newUsers ?? 0) > (data.newUsersLastPeriod ?? 0)) ? 'success.600' : 'danger.600'
                        }}>
                            {data ? `+${data.newUsers}` : '-'}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                            {data ? `(+${data.newUsersLastPeriod})` : ''}
                        </Typography>
                    </Box>
                </Tooltip>
                {data ? (
                    <Tooltip title={`${(((data.newUsers ?? 0) - (data.newUsersLastPeriod ?? 0)) / Math.max(1, (data.newUsersLastPeriod ?? 0)) * 100) > 0 ? 'Прирост' : ((((data.newUsers ?? 0) - (data.newUsersLastPeriod ?? 0)) / Math.max(1, (data.newUsersLastPeriod ?? 0)) * 100) < 0 ? 'Убыль' : 'Без изменений')} пользователей относительно прошлого месяца`} disablePortal={false}>
                        <Box sx={{ ml: 0.5 }}>
                            <Delta current={data.newUsers ?? 0} previous={data.newUsersLastPeriod ?? 0} />
                        </Box>
                    </Tooltip>
                ) : (
                    <Typography level="body-sm" sx={{ mt: 1 }}>-</Typography>
                )}
            </Box>
        </Sheet>
    );
});

UsersCard.displayName = 'UsersCard';


